import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { UserRole } from '../../types/auth'
import { 
  UserCircleIcon, 
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'

interface UserProfileProps {
  className?: string
  onSettingsClick?: () => void
}

const roleColors: Record<UserRole, string> = {
  admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  editor: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  viewer: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
}

const roleIcons: Record<UserRole, React.ReactNode> = {
  admin: <ShieldCheckIcon className="h-4 w-4" />,
  editor: <Cog6ToothIcon className="h-4 w-4" />,
  viewer: <UserCircleIcon className="h-4 w-4" />
}

export function UserProfile({ className = '', onSettingsClick }: UserProfileProps) {
  const { user, logout, isAuthenticated } = useAuth()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  if (!isAuthenticated || !user) {
    return null
  }

  const handleLogout = async () => {
    await logout()
    setIsDropdownOpen(false)
  }

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center space-x-3 px-2 py-1.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        {/* User Avatar */}
        <div className="flex-shrink-0">
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={user.name}
              className="h-8 w-8 rounded-full"
            />
          ) : (
            <div className="h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
              <UserCircleIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
          )}
        </div>

        {/* User Info */}
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {user.name}
          </p>
          <div className="flex items-center mt-1">
            <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${roleColors[user.role]}`}>
              {roleIcons[user.role]}
              <span className="capitalize">{user.role}</span>
            </span>
          </div>
        </div>

        {/* Dropdown Arrow */}
        <ChevronDownIcon 
          className={`h-4 w-4 text-gray-400 transition-transform ${
            isDropdownOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsDropdownOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 backdrop-blur-sm">
            {/* Account Stats Only */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <div>
                  <span className="font-medium">Member since:</span>{' '}
                  {new Date(user.createdAt).toLocaleDateString()}
                </div>
                <div>
                  <span className="font-medium">Last login:</span>{' '}
                  {new Date(user.lastLogin).toLocaleDateString()}
                </div>
              </div>
            </div>


            {/* Actions */}
            <div className="p-2 space-y-1">
              {onSettingsClick && (
                <button
                  onClick={() => {
                    onSettingsClick()
                    setIsDropdownOpen(false)
                  }}
                  className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Cog6ToothIcon className="h-4 w-4" />
                  <span>Settings</span>
                </button>
              )}
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// Compact version for mobile
export function UserProfileCompact({ className = '' }: UserProfileProps) {
  const { user, logout, isAuthenticated } = useAuth()

  if (!isAuthenticated || !user) {
    return null
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* User Avatar */}
      <div className="flex-shrink-0">
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={user.name}
            className="h-6 w-6 rounded-full"
          />
        ) : (
          <div className="h-6 w-6 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
            <UserCircleIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </div>
        )}
      </div>

      {/* User Name */}
      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
        {user.name}
      </span>

      {/* Role Badge */}
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${roleColors[user?.role || 'viewer']}`}>
        {user?.role?.[0]?.toUpperCase()}
      </span>

      {/* Logout Button */}
      <button
        onClick={() => logout()}
        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
        title="Sign Out"
      >
        <ArrowRightOnRectangleIcon className="h-4 w-4" />
      </button>
    </div>
  )
}